import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Modal, ModalProps } from '@/components/ui/modal/Modal';

export type UseModalResp = {
	modal: ReactNode;
	closeModal: () => void;
	openModal: () => void;
	modalBoxClassName?: string;
};

export type UseModalProps = ModalProps & {
	onModalOpen?: () => void;
	onModalClose?: () => void;
};

export const useModal = ({
	children,
	modalBoxClassName,
	onModalClose,
	onModalOpen,
	closeOnOutClick = true,
}: UseModalProps): UseModalResp => {
	const ref = useRef<HTMLDialogElement | null>(null);
	const [isOpen, setIsOpen] = useState(false);

	const closeModal = useCallback(() => {
		if (ref.current) {
			ref.current.close();
			onModalClose && onModalClose();
			setIsOpen(false);
		}
	}, [onModalClose]);

	const openModal = useCallback(() => {
		setIsOpen(true);
		onModalOpen && onModalOpen();
		requestAnimationFrame(() => {
			if (ref.current) {
				ref.current.showModal();
			}
		});
	}, [onModalOpen]);

	useEffect(() => {
		const handleEscape = (event: KeyboardEvent) => {
			if (event.key === 'Escape' && isOpen) {
				event.preventDefault();

				const openDialogs = Array.from(
					document.querySelectorAll('dialog[open]'),
				);

				if (
					openDialogs.length > 0 &&
					openDialogs[openDialogs.length - 1] === ref.current
				) {
					closeModal();
				}
			}
		};

		document.addEventListener('keydown', handleEscape);
		return () => {
			document.removeEventListener('keydown', handleEscape);
		};
	}, [isOpen, closeModal]);

	const modal: ReactNode = isOpen ? (
		<Modal
			close={closeModal}
			ref={ref}
			modalBoxClassName={modalBoxClassName}
			closeOnOutClick={closeOnOutClick}
		>
			{children}
		</Modal>
	) : null;

	return {
		closeModal,
		openModal,
		modal: createPortal(modal, document.body),
	};
};
